/**
 * Tutors management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigationType } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { TutorsDataTable } from '@/components/tutors/inperson/TutorsDataTable';
import { CreateTutorView } from '@/components/tutors/inperson/CreateTutorView';
import { EditTutorView } from '@/components/tutors/inperson/EditTutorView';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { Button } from '@/components/forms/Button';
import {
  type TutorInPersonTabView as TabView,
  TUTOR_INPERSON_TAB_LABELS as TAB_LABELS,
  TUTOR_INPERSON_TAB_COLORS as TAB_COLORS,
  TUTOR_INPERSON_TAB_ICONS as TAB_ICONS,
} from '@/components/tutors/tutor-configs';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Tab ids in display order (sessionStorage must stay in this set). */
const TAB_ORDER: TabView[] = ['tutors', 'pre_registered', 'to_validate', 'to_reassign'];
/** sessionStorage key so browser-back restores the last tab. */
const SESSION_KEY = 'dt-tab:tutors-inperson';

/** In-person tutors list with tab filters and CollapseReplace create/edit. */
export const TutorsManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canExportCsv, canPrevalidate, canValidateAssignment } =
    usePageActions('tutors');
  const queryClient = useQueryClient();
  const navigationType = useNavigationType();
  const { elements: sidebarElements } = useInterfaceElements('sidebar');

  /** Restore the last tab on browser back (`POP`); otherwise default to `tutors`. */
  const [activeTab, setActiveTab] = React.useState<TabView>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem(SESSION_KEY) as TabView | null;
      if (saved && TAB_ORDER.includes(saved)) return saved;
    }
    return 'tutors';
  });

  React.useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, activeTab);
  }, [activeTab]);

  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editTutorId, setEditTutorId] = React.useState<string | null>(null);

  const { viewMode, setViewMode } = useViewMode('tutors-management');
  const { openRowId, openRow, closeRow } = useDetailsView();

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
  }, []);

  /** Close the form and refresh tutor / onboarding / in-person lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
    void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    void queryClient.invalidateQueries({ queryKey: ['tutors-presentiel'] });
  }, [queryClient]);

  /** Stable JSX node for the form — avoids unmounting {@link EditTutorView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateTutorView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editTutorId) {
      return (
        <EditTutorView
          key={editTutorId}
          tutorUserId={editTutorId}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editTutorId, handleFormSuccess, closeForm]);

  const tabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-tutors');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return TAB_ORDER.map((id) => {
      const Icon = id === 'tutors' ? MainIcon : TAB_ICONS[id];
      return {
        id,
        label: TAB_LABELS[id],
        icon: <Icon className="w-3 h-3" />,
        color: TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  return (
    <ManagementPage
      title="Gestion des Tuteurs"
      subtitle="Créez, modifiez et gérez tous vos tuteurs"
      tabs={{
        items: tabItems,
        activeTab,
        onTabChange: (id) => {
          closeForm();
          closeRow();
          setActiveTab(id as TabView);
        },
      }}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              closeRow();
              setActiveForm('create');
            }}
          >
            Créer un tuteur
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <TutorsDataTable
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditTutor={(id) => {
            setEditTutorId(id);
            setActiveForm('edit');
            closeRow();
          }}
          enableFilters={true}
          activeTab={activeTab}
          onTabChange={(id) => {
            closeRow();
            setActiveTab(id);
          }}
          pageActions={{ canEditRow, canExportCsv, canPrevalidate, canValidateAssignment }}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
